import { Modal } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useFetchCountriesQuery, useFetchStatesQuery } from 'common/store/api/rootApi'
import type { Patient } from 'features/Procedure/state/procedure-api.type'
import { useUpdatePatientContactMutation } from 'features/Procedure/state/procedure.api'

import styles from './patient-info-dialog.module.scss'
import { PatientInfoView } from './patient-info-view'
import { PatientContactForm } from './patient-contact-form'
import {
  buildInitialContactValues,
  toContactPayload,
  type PatientContactFormValues,
} from './patient-contact-form.schema'

type Props = {
  isOpen: boolean
  onClose: () => void
  patientInfo: Patient | null
}

const DIALOG_WIDTH = 749

export const PatientInfoDialog = ({ isOpen, onClose, patientInfo }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const { setNotification } = useNotification()
  const [isEditing, setIsEditing] = useState(false)
  const [contactOverride, setContactOverride] = useState<Partial<Patient> | null>(null)
  const [updatePatientContact, { isLoading: isSaving }] = useUpdatePatientContactMutation()

  useFetchCountriesQuery()
  useFetchStatesQuery()

  const patientId = patientInfo?.patient_id

  useEffect(() => {
    setIsEditing(false)
    setContactOverride(null)
  }, [patientId])

  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false)
      setContactOverride(null)
    }
  }, [isOpen])

  const displayPatient = useMemo(() => {
    if (!patientInfo) return null
    return contactOverride ? { ...patientInfo, ...contactOverride } : patientInfo
  }, [patientInfo, contactOverride])

  if (!displayPatient || !patientId) return null

  const handleSubmit = async (values: PatientContactFormValues) => {
    if (!siteId) return

    const payload = toContactPayload(values)

    try {
      await updatePatientContact({ siteId, patientId, data: payload }).unwrap()
      setContactOverride(payload)
      setIsEditing(false)
      setNotification({
        type: ALERT_CONSTANTS.SUCCESS,
        duration: 2,
        title: 'Saved',
        description: 'Patient details updated',
      })
    } catch (error: unknown) {
      const description =
        error instanceof Error ? error.message : 'Failed to update patient details'
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description,
      })
    }
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={DIALOG_WIDTH}
      maskClosable={false}
      classNames={{
        content: styles.modalContent,
      }}>
      {isEditing ? (
        <PatientContactForm
          initialValues={buildInitialContactValues(displayPatient)}
          isSaving={isSaving}
          onBack={() => setIsEditing(false)}
          onSubmit={handleSubmit}
        />
      ) : (
        <PatientInfoView patientInfo={displayPatient} onEdit={() => setIsEditing(true)} />
      )}
    </Modal>
  )
}
